import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import axios from 'axios';
import { toast } from 'react-toastify';
import * as DateHelpers from './DateHelpers';
import { Link } from 'react-router-dom';
import authService from './api-authorization/AuthorizeService';

interface Invoice {
    id: number;
    loading: boolean;
    jobIdSearch: string;
    siteAddressSearch: string;
    jobId: string;
    customerId: number;
    customerName: string;
    siteAddress: string;
    invoiceDate: string;
    dueDate: string;
    invoiceTypeId: number;
    invoiceTypes: GenericList[];
    itemTypes: GenericList[];
    itemList: ItemList[];
    gst: number;
    items: InvoiceItems[];
    invoiceTotal: number;
    invoiceTotalFormatted: string;
    showResults: boolean;
    results: JobSearchResults[],
    errors: any;
}

interface JobSearchResults {
    id: string;
    customer: string;
    siteAddress: string;
    finalQuoteNumber: string;
    status: string;
}

interface InvoiceItems {
    itemIndex: number;
    itemId: number;
    itemTypeId: number;
    purchaseOrderNumber: string;
    chargeGst: boolean;
    amount: string;
    description: string;
    invoiceStartDate: string;
    invoiceEndDate: string;
}

interface GenericList {
    id: number;
    name: string;
}

interface ItemList {
    id: number;
    name: string;
    companyId: number;
    chargeGst: boolean;
}

export class InvoiceData extends React.Component<RouteComponentProps<{}>, Invoice> {
    constructor(props: any) {
        super(props);

        this.state = {
            id: 0,
            loading: true,
            jobIdSearch: "",
            siteAddressSearch: "",
            jobId: "",
            customerId: 0,
            customerName: "",
            siteAddress: "",
            invoiceDate: "",
            dueDate: "",
            invoiceTypeId: 4,
            invoiceTypes: [],
            itemTypes: [],
            itemList: [],
            gst: 0.01,
            items: [{
                itemIndex: 0,
                itemId: -1,
                itemTypeId: -1,
                purchaseOrderNumber: "",
                chargeGst: true,
                amount: "",
                description: "",
                invoiceStartDate: "",
                invoiceEndDate: ""
            }],
            invoiceTotal: 0,
            invoiceTotalFormatted: "$0.00",
            showResults: false,
            results: [],
            errors: {}
        };
    }

    componentDidMount() {
        this.getData();
    }

    async getData() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/User/CheckUserPermission?Permission=CreateInvoice&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data == true) {
                //get invoice types
                axios.get('api/Invoicing/GetNewInvoiceDropdowns?SubId=' + user.sub, {
                    headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
                })
                .then(res => {
                    this.setState({ invoiceTypes: res.data.invoiceTypes, itemTypes: res.data.itemTypes, itemList: res.data.items, gst: res.data.gst, loading: false });
                })
                .catch(error => {
                    toast.error(error.message);
                });
            } else {
                //redirect to 403 permission denied
                this.props.history.push("/accessdenied")
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    search = (e: any) => {
        e.preventDefault();

        //check to make sure at least one search field has been entered
        if (this.state.jobIdSearch || this.state.siteAddressSearch) {
            this.searchJobs();
        } else {
            this.setState({ showResults: false });
            toast.error("Please enter at least one search item");
        }

    }

    async searchJobs() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/Job/SearchJobs?SubId=' + user.sub + '&JobId=' + this.state.jobIdSearch + '&CustomerId=&QuoteNo=&SiteAddress=' + encodeURIComponent(this.state.siteAddressSearch) + '&Suburb=&SiteVisit=&StatusId=', {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            this.setState({ showResults: true, results: res.data });
        })
    }

    selectJob = (jobId: string, customer: string, siteAddress: string) => {
        var errors = this.state.errors;
        delete errors["jobId"];

        //load new invoice based on job selected
        this.setState({ jobId: jobId, customerName: customer, siteAddress: siteAddress, jobIdSearch: "", siteAddressSearch: "", showResults: false, results: [], errors: errors });
    }

    handleChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors[e.target.name];
        }

        let change: any = {};
        change[e.target.name] = e.target.value;
        change["errors"] = errors;
        this.setState(change);
    }

    onCellChange = (index: number, item: string, value: any) => {
        var items = this.state.items;
        var lastrow = items.length - 1 == index;

        var invoiceItem = items[index];
        let newInvoiceItem: any = {};
        newInvoiceItem = invoiceItem;
        newInvoiceItem[item] = value;

        var invoiceTotal = this.state.invoiceTotal;
        var invoiceTotalFormatted = "";
        if (item == "itemId") {
            //need to set whether or not the item charges gst
            var itemlist = this.state.itemList;
            var foundItem = itemlist.find(i => i.id == value);
            invoiceItem.chargeGst = (foundItem == null) ? true : foundItem.chargeGst;
        }
        if (item == "amount") {
            invoiceTotal = 0;   //reset

            //recalc total
            for (var i = 0; i < items.length - 1; i++) {
                invoiceTotal += parseFloat(items[i].amount);
            }
        }
        invoiceTotalFormatted = (invoiceTotal).toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });

        //check if this row is the last row, and if there is a value set and if so add a new blank row
        if (lastrow) {
            this.addBlankLine(items);
        }

        var errors = this.state.errors;
        delete errors["invoiceItemsCheck"];
        delete errors["invoiceItems"];

        this.setState({ items: items, invoiceTotal: invoiceTotal, invoiceTotalFormatted: invoiceTotalFormatted, errors: errors });
    }

    addBlankLine = (items: InvoiceItems[]) => {
        items.push({
            itemIndex: items.length,
            itemId: -1,
            itemTypeId: -1,
            purchaseOrderNumber: "",
            chargeGst: true,
            amount: "",
            description: "",
            invoiceStartDate: "",
            invoiceEndDate: ""
        });
    }

    deleteInvoiceItem = (e: any, index: number) => {
        e.preventDefault();

        var items = this.state.items;
        items.splice(index, 1);

        this.setState({ items: items });
    }

    validate = () => {
        //make sure all screens have valid data before saving
        let fields = this.state;
        let errors: any = {};
        let formIsValid = true;
        var validDate = true;

        if (!fields.jobId) {
            formIsValid = false;
            errors["jobId"] = "You must select a Job!";
        }

        if (fields.invoiceDate == "") {
            validDate = false;
            formIsValid = false;
            errors["invoiceDate"] = "Invoice Date is required";
        } else if (!DateHelpers.isValidDate(fields.invoiceDate)) {
            validDate = false;
            formIsValid = false;
            errors["invoiceDate"] = "Valid Invoice Date is required";
        } else if (DateHelpers.daysBetweenDates(DateHelpers.getCurrentDate(), fields.invoiceDate) < -14) {
            validDate = false;
            formIsValid = false;
            errors["invoiceDate"] = "Invoice Date must be within 14 days";
        }

        if (fields.dueDate == "") {
            validDate = false;
            formIsValid = false;
            errors["dueDate"] = "Due Date is required";
        } else if (!DateHelpers.isValidDate(fields.dueDate)) {
            validDate = false;
            formIsValid = false;
            errors["dueDate"] = "Valid Due Date is required";
        }

        if (validDate && !DateHelpers.compareDate(fields.invoiceDate, fields.dueDate)) {
            formIsValid = false;
            errors["dueDate"] = "Due Date must be >= Invoice Date";
        }

        if (fields.invoiceTypeId < 0) {
            formIsValid = false;
            errors["invoiceTypeId"] = "Invoice Type is required";
        }

        //validate items
        if (fields.items.length <= 1) {
            formIsValid = false;
            errors["invoiceItemsCheck"] = "At least one item is required";
        } else {
            //check to make sure each item entered is valid
            var rowNumber = 0;

            var errorString = "Please enter the following values: ";
            var error = false;

            for (var i = 0; i < (fields.items.length - 1); i++) {
                var validHireDate = true;
                rowNumber += 1;
                var newErrorString = "";

                if (fields.items[i].itemTypeId < 1) {
                    newErrorString += "Type, ";
                    error = true;
                }
                if (fields.items[i].itemId < 1) {
                    newErrorString += "Item, ";
                    error = true;
                }
                if (!fields.items[i].description) {
                    newErrorString += "Description, ";
                    error = true;
                }
                if (!fields.items[i].invoiceStartDate) {
                    validHireDate = false;
                    newErrorString += "Hire Start, ";
                    error = true;
                } else if (!DateHelpers.isValidDate(fields.items[i].invoiceStartDate)) {
                    validHireDate = false;
                    newErrorString += " Valid Hire Start, ";
                    error = true;
                }
                if (!fields.items[i].invoiceEndDate) {
                    validHireDate = false;
                    newErrorString += "Hire End, ";
                    error = true;
                } else if (!DateHelpers.isValidDate(fields.items[i].invoiceEndDate)) {
                    validHireDate = false;
                    newErrorString += " Valid Hire End, ";
                    error = true;
                }
                if (validHireDate && !DateHelpers.compareDate(fields.items[i].invoiceStartDate, fields.items[i].invoiceEndDate)) {
                    newErrorString += " Hire End must be >= Hire Start, ";
                    error = true;
                }
                if (fields.items[i].amount.length < 0.1) {
                    newErrorString += "Amount, ";
                    error = true;
                }

                if (newErrorString != "") {
                    errorString += "\n"
                    errorString += "Row " + rowNumber + ": " + newErrorString;
                    errorString = errorString.substring(0, errorString.length - 2);
                }
                
            }
            if (error) {
                formIsValid = false;
                errors["invoiceItems"] = errorString;
            }

        }
        
        this.setState({ errors: errors });
        return formIsValid;
    }


    save = (e: any) => {
        e.preventDefault();
        if (this.validate()) {
            this.saveInvoice();
        }

    }

    async saveInvoice() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var items = this.state.items;
        //remove blank line
        items.splice(this.state.items.length - 1, 1);

        var invoice = {
            jobId: this.state.jobId,
            customerId: this.state.customerId,
            invoiceDate: this.state.invoiceDate,
            dueDate: this.state.dueDate,
            invoiceTypeId: this.state.invoiceTypeId,
            items: items,
            subId: user.sub
        };

        axios.post('api/Invoicing/Save', invoice, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            toast.success("Invoice Saved");
            this.props.history.push("/invoices");
        })
        .catch(error => {
            var items = this.state.items;
            this.addBlankLine(items);
            this.setState({ items: items });
            toast.error(error.message);
        });
    }

    public render() {
        let contents = this.state.loading
            ? <p><em>Loading...</em></p>
            : this.renderDetails();

        return <div>
            <h1>New Invoice</h1>
            {contents}
        </div>;
    }

    renderDetails = () => {
        var invoiceTypes = this.state.invoiceTypes;
        var invoiceItems = this.state.items;
        var itemTypes = this.state.itemTypes;
        var itemList = this.state.itemList;

        return (
            <form name="invoice" onSubmit={this.save}>
                <div className="makeitflex"> 
                    <label className="input-group" htmlFor="jobIdSearch">
                        <span className="label">Job Id</span>
                        <input className="input" type="text" id="jobIdSearch" name="jobIdSearch" value={this.state.jobIdSearch} onChange={(e) => this.handleChange(e)}></input>
                    </label>
                    <label className="input-group marginLeft10 marginRight10" htmlFor="siteAddressSearch">
                        <span className="label label--setsizesm">Site Address</span>
                        <input className="input" type="text" id="siteAddressSearch" name="siteAddressSearch" value={this.state.siteAddressSearch} onChange={(e) => this.handleChange(e)}></input>
                    </label>
                    <button className="defaultbutton marginBottom10" type='button' onClick={this.search}>Search</button>
                </div>
                <div className={this.state.showResults ? "" : "hidden"}>
                    <div className={this.state.results.length > 0 ? 'overflowAuto' : 'hidden'}>
                        <table className='table tableColours'>
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>Job Id</th>
                                    <th>Site Address</th>
                                    <th>Customer</th>
                                    <th>Quote Number</th>
                                    <th>Status</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {this.state.results.map(job =>
                                    <tr key={job.id}>
                                        <td><input type='checkbox' className="input" checked={false} onChange={(e) => this.selectJob(job.id, job.customer, job.siteAddress)} /></td>
                                        <td>{job.id}</td>
                                        <td>{job.siteAddress}</td>
                                        <td>{job.customer}</td>
                                        <td>{job.finalQuoteNumber}</td>
                                        <td>{job.status}</td>
                                        <td>
                                            <Link to={'/job/' + job.id} target="_blank" >
                                                Open
                                            </Link>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <span className={this.state.results.length == 0 ? "" : "hidden"}>There were no matching jobs found. Try modifying your search criteria.</span>
                </div>
                <div className="row">
                    <hr></hr>
                </div>
                <div>
                    <label className="input-group" htmlFor="jobId">
                        <span className="label">Job Id</span>
                        {this.state.jobId}
                    </label>
                    <span className="label errors">{this.state.errors["jobId"]}</span>
                </div>
                <div>
                    <label className="input-group" htmlFor="customerName">
                        <span className="label">Customer</span>
                        {this.state.customerName}
                    </label>
                </div>
                <div>
                    <label className="input-group" htmlFor="siteAddress">
                        <span className="label">Site Address</span>
                        {this.state.siteAddress}
                    </label>
                </div>
                <div>
                    <label className="input-group" htmlFor="invoiceDate">
                        <span className="label">Invoice Date</span>
                        <input type='date' id="invoiceDate" name="invoiceDate" className="input" value={this.state.invoiceDate} onChange={(e) => this.handleChange(e)} />
                    </label>
                    <span className="label errors">{this.state.errors["invoiceDate"]}</span>
                </div>
                <div>
                    <label className="input-group" htmlFor="dueDate">
                        <span className="label">Due Date</span>
                        <input type='date' id="dueDate" name="dueDate" className="input" value={this.state.dueDate} onChange={(e) => this.handleChange(e)} />
                    </label>
                    <span className="label errors">{this.state.errors["dueDate"]}</span>
                </div>
                <div>
                    <label className="input-group" htmlFor="invoiceTypeId">
                        <span className="label">Invoice Type</span>
                        <select className="select" id="invoiceTypeId" name="invoiceTypeId" value={this.state.invoiceTypeId} onChange={(e) => this.handleChange(e)}>
                            <option hidden defaultValue="-1"></option>
                            {invoiceTypes.map(type =>
                                <option key={type.id} value={type.id}>{type.name}</option>
                            )};
                        </select>
                    </label>
                    <span className="label errors">{this.state.errors["invoiceTypeId"]}</span>
                </div>
                <div className="table__margin overflowAuto">
                    <table className='table subtable subtable__large tableColours'>
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Item</th>
                                <th>P/O # </th>
                                <th>Description</th>
                                <th>Hire Start</th>
                                <th>Hire End</th>
                                <th>Amount</th>
                                <th></th>
                            </tr>
                        </thead>
                        {invoiceItems.map((item, index) =>
                            <tbody key={index}>
                                <tr>
                                    <td className="table__text--align">
                                        <select className="select table__select--size" id="itemTypeId" name="itemTypeId" value={item.itemTypeId} onChange={(e) => this.onCellChange(index, "itemTypeId", e.target.value)} >
                                            <option hidden defaultValue="-1"></option>
                                            {itemTypes.map(type =>
                                                <option key={type.id} value={type.id}>{type.name}</option>
                                            )};
                                        </select>
                                    </td>
                                    <td className="table__text--align">
                                        <select className="select table__select--size" id="itemId" name="itemId" value={item.itemId} onChange={(e) => this.onCellChange(index, "itemId", e.target.value)} >
                                            <option hidden defaultValue="-1"></option>
                                            {itemList.map(type =>
                                                <option key={type.id} value={type.id}>{type.name}</option>
                                            )};
                                        </select>
                                    </td>
                                    <td className="table__text--align">
                                        <input type="text" className="form-control invoicing__poSize" value={item.purchaseOrderNumber} onChange={(e) => this.onCellChange(index, "purchaseOrderNumber", e.target.value)} />
                                    </td>
                                    <td className="table__text--align">
                                        <input type="text" className="form-control" value={item.description} onChange={(e) => this.onCellChange(index, "description", e.target.value)} />
                                    </td>
                                    <td className="table__text--align">
                                        <input type="date" className="form-control" value={item.invoiceStartDate} onChange={(e) => this.onCellChange(index, "invoiceStartDate", e.target.value)} />
                                    </td>
                                    <td className="table__text--align">
                                        <input type="date" className="form-control" value={item.invoiceEndDate} onChange={(e) => this.onCellChange(index, "invoiceEndDate", e.target.value)} />
                                    </td>
                                    <td className="table__text--align table__input--alignright">
                                        <input type='number' min="0" step="0.01" className="form-control table__input--alignright table__input--min100" value={item.amount} onChange={(e) => this.onCellChange(index, "amount", e.target.value)} />
                                    </td>
                                    <td className="table__text--align">
                                        {(index != (invoiceItems.length - 1)) ?
                                            <div className="delete--tablecell">
                                                <a className="makeitred" href="#" onClick={(e) => this.deleteInvoiceItem(e, index)}>
                                                    <span className="fas fa-times-circle edit--icon alignIconCenter"></span>
                                                </a>
                                            </div> : ""
                                        }
                                    </td>
                                </tr>
                            </tbody>
                        )}
                    </table>
                    <span className="label errors">{this.state.errors["invoiceItemsCheck"]}</span>
                    {this.state.errors["invoiceItems"] ?
                        (this.state.errors["invoiceItems"]).split("\n").map((item: any, key: any) => {
                            return <span className="label errors" key={key}>{item}<br /></span>
                        })
                        : ""}
                </div>
                <div>
                    <button className="defaultbutton" type="submit">Save</button>
                </div>
            </form>
        );
    }

}
